import { Plus, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFetchPickupAddresses, useUpdatePickupAddress } from '@/hooksApi/usePickupAddress';
import { useQueryClient } from '@tanstack/react-query';
import { PickupAddress } from '@/types';
import { LocationRow } from './LocationRow';
import { toast } from '@/hooks/use-toast';
import { SettingsListLoading } from '@/components/SettingsListLoading';
import { SettingsBreadcrumb } from '@/components/SettingsBreadcrumb';
import { ROUTE } from '@/config';

const SettingsLocations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useFetchPickupAddresses();
  const { mutate: updateAddress } = useUpdatePickupAddress();

  const addresses = data?.object?.items ?? [];

  const handleSetDefault = (address: PickupAddress) => {
    updateAddress(
      { uid: address.uid, data: { default: true } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['pickupAddresses']);
          toast({ title: t('default_address_updated') });
        },
        onError: (error: any) => {
          const msg = error?.response?.data?.message || error?.message || t('operation_failed');
          toast({ title: t('error_title'), description: msg, variant: 'destructive' });
        },
      },
    );
  };

  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
        <SettingsBreadcrumb moduleName='locations' moduleRoute={ROUTE.SETTINGS_LOCATIONS} />

        <div className='flex items-center justify-between'>
          <h1 className='text-xl font-semibold text-sd-foreground m-0'>{t('locations')}</h1>
          <button
            onClick={() => navigate('/settings/locations/new')}
            className='flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity'
          >
            <Plus className='h-4 w-4' />
            {t('add_address')}
          </button>
        </div>

        {isLoading ? (
          <SettingsListLoading />
        ) : addresses.length === 0 ? (
          <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-8 text-center'>
            <MapPin className='h-10 w-10 mx-auto text-sd-muted-foreground mb-3' />
            <p className='text-sm text-sd-muted-foreground'>{t('no_addresses_yet')}</p>
          </div>
        ) : (
          <div className='space-y-3'>
            {addresses.map((address) => (
              <LocationRow
                key={address.uid}
                address={address}
                onEdit={(uid) => navigate(`/settings/locations/${uid}/edit`)}
                onSetDefault={handleSetDefault}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsLocations;
